import { supabase } from '@/core/database/supabase';

export interface SystemSettings {
  siteName: string;
  siteDescription?: string;
  supportEmail: string;
  hotline: string;
  footerText?: string;
}

export const settingsService = {
  async getGeneralSettings(): Promise<SystemSettings> {
    const { data, error } = await supabase
      .from('whitelabel_configs')
      .select('*')
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') { // PGRST116 is 'no rows'
      throw error;
    }

    if (!data) {
      return {
        siteName: 'Landscape SaaS',
        siteDescription: 'Nền tảng quản lý dự án và bảo dưỡng cây xanh thông minh',
        supportEmail: 'support@landscape.vn',
        hotline: '1900 123 456',
        footerText: '© 2026 Landscape. All rights reserved.',
      };
    }

    return {
      siteName: data.brand_name || '',
      siteDescription: data.custom_css || '', // Using custom_css as placeholder if no desc field
      supportEmail: data.support_email || '',
      hotline: data.support_phone || '',
      footerText: data.footer_text || '',
    };
  },

  async updateGeneralSettings(settings: SystemSettings) {
    // Check if record exists
    const { data: existing } = await supabase
      .from('whitelabel_configs')
      .select('id')
      .limit(1)
      .maybeSingle();

    const payload = {
      brand_name: settings.siteName,
      support_email: settings.supportEmail,
      support_phone: settings.hotline,
      footer_text: settings.footerText,
      custom_css: settings.siteDescription, // Re-using existing field for now
      is_active: true,
      updated_at: new Date().toISOString(),
    };

    if (existing?.id) {
      const { error } = await supabase
        .from('whitelabel_configs')
        .update(payload)
        .eq('id', existing.id);
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from('whitelabel_configs')
        .insert([payload]);
      if (error) throw error;
    }
  },

  async getSecuritySettings() {
    const { data, error } = await supabase
      .from('feature_flags')
      .select('config')
      .eq('feature_key', 'system_security_policy')
      .maybeSingle();

    if (error) throw error;

    if (!data?.config) {
      return {
        passwordMinLength: 8,
        requireSpecialChar: true,
        requireNumber: true,
        require: true,
        maxActiveSessions: 3,
        sessionTimeout: 60,
        force2FA: false,
      };
    }

    return data.config;
  },

  async updateSecuritySettings(config: any) {
    const { data: existing } = await supabase
      .from('feature_flags')
      .select('id')
      .eq('feature_key', 'system_security_policy')
      .maybeSingle();

    const payload = {
      feature_key: 'system_security_policy',
      is_enabled: true,
      config: config,
      plan_code: 'all',
    };

    if (existing?.id) {
      const { error } = await supabase
        .from('feature_flags')
        .update({ config: config })
        .eq('id', existing.id);
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from('feature_flags')
        .insert([payload]);
      if (error) throw error;
    }
  },

  async getLocalizationSettings() {
    const { data, error } = await supabase
      .from('feature_flags')
      .select('config')
      .eq('feature_key', 'system_localization_config')
      .maybeSingle();

    if (error) throw error;

    if (!data?.config) {
      return {
        defaultLanguage: 'vi',
        timezone: 'Asia/Ho_Chi_Minh',
        dateFormat: 'DD/MM/YYYY',
        currency: 'VND',
        numberFormat: 'comma-decimal',
      };
    }

    return data.config;
  },

  async updateLocalizationSettings(config: any) {
    const { data: existing } = await supabase
      .from('feature_flags')
      .select('id')
      .eq('feature_key', 'system_localization_config')
      .maybeSingle();

    const payload = {
      feature_key: 'system_localization_config',
      is_enabled: true,
      config: config,
      plan_code: 'all',
    };

    if (existing?.id) {
      const { error } = await supabase
        .from('feature_flags')
        .update({ config: config })
        .eq('id', existing.id);
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from('feature_flags')
        .insert([payload]);
      if (error) throw error;
    }
  },

  async getInfraSettings() {
    const { data, error } = await supabase
      .from('feature_flags')
      .select('config')
      .eq('feature_key', 'system_infrastructure_config')
      .maybeSingle();

    if (error) throw error;

    if (!data?.config) {
      return {
        maintenanceMode: false,
        maintenanceMessage: 'Hệ thống đang bảo trì định kỳ. Vui lòng quay lại sau.',
        logLevel: 'info',
        cacheTTL: 60,
        enableAnalytics: true,
      };
    }

    return data.config;
  },

  async updateInfraSettings(config: any) {
    const { data: existing } = await supabase
      .from('feature_flags')
      .select('id')
      .eq('feature_key', 'system_infrastructure_config')
      .maybeSingle();

    const payload = {
      feature_key: 'system_infrastructure_config',
      is_enabled: true,
      config: config,
      plan_code: 'all',
    };

    if (existing?.id) {
      const { error } = await supabase
        .from('feature_flags')
        .update({ config: config })
        .eq('id', existing.id);
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from('feature_flags')
        .insert([payload]);
      if (error) throw error;
    }
  },

  async getAdvancedSettings() {
    const { data, error } = await supabase
      .from('feature_flags')
      .select('config')
      .eq('feature_key', 'system_advanced_config')
      .maybeSingle();

    if (error) throw error;

    if (!data?.config) {
      return {
        customCss: '',
        customHeaderScripts: '',
        customBodyScripts: '',
        debugMode: false,
        enableExperimentalFeatures: false,
      };
    }

    return data.config;
  },

  async updateAdvancedSettings(config: any) {
    const { data: existing } = await supabase
      .from('feature_flags')
      .select('id')
      .eq('feature_key', 'system_advanced_config')
      .maybeSingle();

    const payload = {
      feature_key: 'system_advanced_config',
      is_enabled: true,
      config: config,
      plan_code: 'all',
    };

    if (existing?.id) {
      const { error } = await supabase
        .from('feature_flags')
        .update({ config: config })
        .eq('id', existing.id);
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from('feature_flags')
        .insert([payload]);
      if (error) throw error;
    }
  }
};
